import { useState, type ReactNode } from 'react';
import { track } from '../lib/analytics';

type IconProps = { className?: string };

function PotIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 9h16v6a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V9z" />
      <path d="M2 9h20M8 9V6m8 3V6" strokeLinecap="round" />
    </svg>
  );
}
function CalendarIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18M8 2v4M16 2v4" strokeLinecap="round" />
    </svg>
  );
}
function FamilyIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="8" cy="8" r="3" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M2 20c0-3.3 2.7-6 6-6s6 2.7 6 6M14.5 20c0-2.5 1.6-4.5 4-4.5" strokeLinecap="round" />
    </svg>
  );
}

interface Slide {
  Icon: (props: IconProps) => ReactNode;
  title: string;
  subtitle: string;
}

const SLIDES: Slide[] = [
  {
    Icon: PotIcon,
    title: 'Что приготовить — решим за минуту',
    subtitle: 'Отметьте, что есть дома, — подскажем, что приготовить из ваших продуктов.',
  },
  {
    Icon: CalendarIcon,
    title: 'Меню на неделю и список покупок',
    subtitle: 'Соберите меню на 7 дней в пару касаний — список покупок составится сам, и подскажем, чего не хватает.',
  },
  {
    Icon: FamilyIcon,
    title: 'Готовим для всей семьи',
    subtitle: 'Учтём, кто что не ест: аллергии и предпочтения каждого.',
  },
];

export function Onboarding({ onFinish, onSkip }: { onFinish: () => void; onSkip: () => void }) {
  const [index, setIndex] = useState(0);
  const isLast = index === SLIDES.length - 1;
  const { Icon, title, subtitle } = SLIDES[index]!;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Знакомство с приложением"
      className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-cream px-6 pb-[calc(2rem+env(safe-area-inset-bottom))] pt-[calc(1.5rem+env(safe-area-inset-top))]"
    >
      <div className="flex justify-end">
        <button
          onClick={() => {
            track('onboarding_skip');
            onSkip();
          }}
          className="rounded-lg px-3 py-1.5 text-sm font-medium text-ink/55 transition active:scale-[0.98]"
        >
          Пропустить
        </button>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center text-center">
        <div className="mb-8 flex h-28 w-28 items-center justify-center rounded-full bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-100">
          <Icon className="h-14 w-14" />
        </div>
        <h1 className="mb-3 text-[26px] font-bold leading-tight text-ink">{title}</h1>
        <p className="max-w-xs text-[15px] leading-relaxed text-ink/70">{subtitle}</p>
      </div>

      <div className="mb-6 flex justify-center gap-2" role="tablist" aria-label="Слайды">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={i === index}
            aria-label={`Слайд ${i + 1}`}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all ${i === index ? 'w-6 bg-primary-600' : 'w-2 bg-primary-600/25'}`}
          />
        ))}
      </div>

      <button
        onClick={() => {
          if (isLast) {
            track('onboarding_done');
            onFinish();
          } else {
            setIndex((i) => i + 1);
          }
        }}
        className="w-full rounded-xl bg-primary-600 px-4 py-3.5 font-semibold text-white shadow-card transition active:scale-[0.98]"
      >
        {isLast ? 'Начать' : 'Далее'}
      </button>
    </div>
  );
}
